import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../../../core/pagination/datatable";
import { Spin, Modal, Input } from "antd";
import Swal from "sweetalert2";
import EditFunctionalLocations from "./EditBlock";
import AddFunctionalLocations from "./AddBlock";
import {
  getApiBaseUrl,
  GET_LOCATIONS,
  UPDATE_LOCATION_STATUS,
  ADD_ACTIVITY,
  LOGIN,
} from "../../Config/Config";

const FunctionalLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [reauthVisible, setReauthVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  console.log(selectedRecord);
  const fetchLocations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}${GET_LOCATIONS}`);
      const result = await response.json();
      setLocations(result?.ResultData || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  // Log activity to the backend
  const logActivity = async (isSuccess, note) => {
    try {
      await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ActivityType: 1,
          PerformedBy: user.UserID || 0,
          PerformedOn: new Date().toISOString().slice(0, 19).replace("T", " "),
          Notes: note,
          Location: "deleteBlock",
          IsSucces: isSuccess ? 1 : 0,
        }),
      });
    } catch (err) {
      console.error("Error logging activity:", err);
    }
  };

  // Handle password submission for reauthentication
  const handlePasswordSubmit = async () => {
    if (!password) {
      setPasswordError("Please enter your password");
      return;
    }

    setIsLoading(true);
    setPasswordError("");
    const username = user?.UserName || user?.Username;

    try {
      const loginRes = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const loginData = await loginRes.json();

      if (loginData?.Message === "Login successful") {
        setReauthVisible(false);
        setPassword("");
        await handleDeleteBlockConfirmed(selectedRecord);
      } else {
        setPasswordError("Reauthentication failed. Please check your password.");
        await logActivity(
          0,
          `Failed reauthentication for deleting Block "${selectedRecord?.BlockName}" by ${username}`
        );
      }
    } catch (error) {
      console.error("Reauthentication error:", error);
      setPasswordError("Something went wrong. Please try again.");
      await logActivity(
        0,
        `Reauthentication error for deleting Block "${selectedRecord?.BlockName}" by ${username}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete action after reauthentication
  const handleDeleteBlockConfirmed = async (record) => {
    const confirmResult = await Swal.fire({
      title: `Delete "${record.BlockName}"?`,
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
    });

    if (!confirmResult.isConfirmed) return;

    setIsLoading(true);
    const userId = user?.UserID || 0;

    const payload = {
      BlockId: record.BlockId,
      UpdatedBy: userId,
      IsActive: 0,
    };

    let messageText = "";

    try {
      const response = await fetch(
        `${getApiBaseUrl()}${UPDATE_LOCATION_STATUS}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (result?.Status) {
        messageText = `Block "${record.BlockName}" deleted successfully`;

        await Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: messageText,
          confirmButtonColor: "#3085d6",
        });
        await logActivity(1, `Deleted Block "${record.BlockName}"`);
      } else {
        messageText = result?.Message || "Failed to delete";

        await Swal.fire({
          icon: "error",
          title: "Delete Failed",
          text: messageText,
          confirmButtonColor: "#3085d6",
        });
        await logActivity(0, `Failed to delete Block "${record.BlockName}"`);
      }
    } catch (error) {
      console.error("Error deleting block:", error);
      messageText = "Failed to delete block";

      await Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: messageText,
        confirmButtonColor: "#3085d6",
      });
      await logActivity(0, `Failed to delete Block "${record.BlockName}"`);
    } finally {
      fetchLocations();
      setIsLoading(false);
    }
  };

  // Trigger reauthentication before delete
  const handleDeleteBlock = (record) => {
    setSelectedRecord(record);
    setReauthVisible(true);
  };

  const columns = [
    {
      title: "Block Name",
      dataIndex: "BlockName",
      key: "BlockName",
      sorter: (a, b) => a.BlockName.localeCompare(b.BlockName),
    },
    {
      title: "Description",
      dataIndex: "Description",
      key: "Description",
      sorter: (a, b) => a.Description.localeCompare(b.Description),
    },
    {
      title: "Status",
      dataIndex: "IsActive",
      key: "IsActive",
      render: (isActive) => (
        <span className={`badge ${isActive ? "bg-success" : "bg-danger"}`}>
          {isActive ? "Active" : "Inactive"}
        </span>
      ),
      sorter: (a, b) => a.IsActive - b.IsActive,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action d-flex align-items-center">
            <Link
              to="#"
              className="me-2"
              data-bs-toggle="offcanvas"
              data-bs-target="#EditBlocksOffcanvas"
              aria-controls="EditBlocksOffcanvas"
              onClick={() => setSelectedRecord(record)}
              title="Edit"
            >
              <i data-feather="edit" className="feather-edit text-info"></i>
            </Link>
            <Link
              to="#"
              className="me-2"
              onClick={() => handleDeleteBlock(record)}
              title="Delete"
            >
              <i data-feather="trash" className="feather-trash text-danger"></i>
            </Link>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)", // subtle tint
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}

      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/assetmgt/dashboard">
                        <i className="fas fa-home fa-lg" />
                      </Link>
                    </li>
                    <h4 className="breadcrumb-item " aria-current="page">
                      Blocks{" "}
                    </h4>
                  </ol>
                </nav>
                <h6>Manage your Blocks here</h6>
              </div>
            </div>
            <div className="page-btn">
              <Link
                data-bs-toggle="offcanvas"
                data-bs-target="#AddBlocksOffcanvas"
                aria-controls="AddBlocksOffcanvas"
                className="btn btn-success btn-sm"
              >
                <i className="ti ti-circle-plus fs-4"></i>
              </Link>
            </div>
          </div>

          <div>
            <div className="card table-list-card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3  ">
                <div className="search-set mt-4"></div>
                <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3"></div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  {loading ? (
                    <div className="text-center my-4">
                      <Spin size="large" />
                      <p>Loading data...</p>
                    </div>
                  ) : locations.length > 0 ? (
                    <Table dataSource={locations} columns={columns} />
                  ) : (
                    <p className="text-center my-4">No data available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <>
          <EditFunctionalLocations editObj={selectedRecord} />
          <AddFunctionalLocations />
          <Modal
            title="Confirm Password"
            open={reauthVisible}
            onOk={handlePasswordSubmit}
            onCancel={() => {
              setReauthVisible(false);
              setPassword("");
              setPasswordError("");
            }}
            confirmLoading={isLoading}
            width={350}
          >
            <Spin spinning={isLoading}>
              <Input.Password
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                onCopy={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
              />
              {passwordError && (
                <div className="text-danger mt-2">{passwordError}</div>
              )}
            </Spin>
          </Modal>
        </>
      </div>
    </>
  );
};

export default FunctionalLocations;